import { useLocation, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, BookmarkPlus, Calendar, Check, ChevronDown, ChevronRight, CircleQuestionMark, ClipboardCheck, Copy, FileDown, FileText } from 'lucide-react';
import type React from 'react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import Spinner from '@/components/shared/Spinner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FileWordIcon } from '@/components/ui/icons';
import { Button as NewButton } from '@/components/ui/NewButton';
import type { Message, QuickReplyQuestion } from '@/hooks/useChat';
import type { Document } from '@/hooks/useDocuments';
import { backendClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { collectWebCitationMeta, type WebCitationDisplay } from '@/utils/citation-utils';
import { exportMarkdownAsDocx } from '@/utils/docx-export';
import { QuotedExcerpt, quotedPartsFromMessage } from './QuotedExcerpt';
import { TurnTimeline } from './TurnTimeline';
import {
  answerTextFromTimeline,
  timelineFromCompletedMessage,
  timelineWaitMarkersFromMetadata,
} from './assistantTurnTimeline';
import { QuickRepliesPanel, type QuickReplyAnswer } from './QuickReplies';
import { TemplateChoicesPanel } from './TemplateChoices';
import {
  templateChoiceFromMetadata,
  type ActiveTemplateChoices,
  type TemplateChoiceSelection,
} from '@/components/case-partner/template-choices';

type DraftDocumentRow = Record<string, unknown> & {
  draft_document_guid?: string;
  draft_document_job_guid?: string;
  draft_document_title?: string;
  draft_document_version?: number;
  draft_document_action?: string;
  draft_document_job_status?: string;
  draft_document_job_progress_label?: string | null;
  draft_document_job_progress_detail?: Record<string, unknown> | null;
  draft_document_job_error?: string | null;
};

interface MessageItemProps {
  message: Message;
  documents: Document[] | undefined;
  matterGuid?: string;
  chatGuid?: string | null;
  slug: string;
  isCopied: boolean;
  onCopy: (content: string, id: string | number | undefined) => void;
  onSaveToLibrary?: (content: string) => void;
  /** Render function for cited documents - allows parent to use memoized components */
  renderCitedDocs?: (
    content: string,
    webCitationMeta?: ReadonlyMap<string, WebCitationDisplay>,
  ) => React.ReactNode;
  wordExportContent?: string;
  wordExportFilename?: string;
  wordExportIncludeCitations?: boolean;
  draftCardsDefaultExpanded?: boolean;
  quickReplyQuestions?: QuickReplyQuestion[];
  quickReplyDisabled?: boolean;
  onQuickReplySubmit?: (value: string, answers: QuickReplyAnswer[]) => void;
  templateChoices?: ActiveTemplateChoices | null;
  templateChoiceDisabled?: boolean;
  onTemplateChoiceSubmit?: (value: string, selection: TemplateChoiceSelection) => void;
  /** 'compact' when Case Partner is rendered in the side panel. */
  variant?: 'full' | 'compact';
}

const quickReplyAnswersFromMetadata = (metadata: Record<string, unknown>): QuickReplyAnswer[] => {
  const quickReplyAnswers = isRecord(metadata.quick_reply_answers)
    ? metadata.quick_reply_answers
    : null;
  const answers = Array.isArray(quickReplyAnswers?.answers)
    ? quickReplyAnswers.answers
    : [];

  return answers
    .map((answer): QuickReplyAnswer | null => {
      if (!isRecord(answer)) return null;
      const question = stringValue(answer.question)?.trim();
      const value = stringValue(answer.answer)?.trim();
      return question && value ? { question, answer: value } : null;
    })
    .filter((answer): answer is QuickReplyAnswer => Boolean(answer));
};

const QuickReplyAnswerSummary: React.FC<{ answers: QuickReplyAnswer[] }> = ({ answers }) => (
  <div className="w-full max-w-xl rounded-lg border border-slate-200/80 bg-slate-50/70 px-3 py-2 text-left shadow-[0_6px_18px_rgba(38,52,93,0.045)] sm:min-w-72">
    <div className="mb-1 text-[10px] font-semibold uppercase leading-4 tracking-[0.08em] text-slate-400">
      Answers
    </div>
    <div className="divide-y divide-slate-200/70">
      {answers.map((answer) => (
        <div
          key={`${answer.question}:${answer.answer}`}
          className="grid grid-cols-1 gap-1 py-1.5 sm:grid-cols-[minmax(8rem,0.9fr)_minmax(0,1.35fr)] sm:gap-3"
        >
          <div
            className="line-clamp-2 text-[11px] font-medium leading-4 text-slate-500"
            title={answer.question}
          >
            {answer.question}
          </div>
          <div className="min-w-0 break-words text-[12px] font-semibold leading-4 text-slate-700">
            {answer.answer}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const TemplateChoiceAnswerSummary: React.FC<{ selection: TemplateChoiceSelection }> = ({ selection }) => (
  <div className="w-full max-w-xl rounded-lg border border-slate-200/80 bg-slate-50/70 px-3 py-2 text-left shadow-[0_6px_18px_rgba(38,52,93,0.045)] sm:min-w-72">
    <div className="mb-1 text-[10px] font-semibold uppercase leading-4 tracking-[0.08em] text-slate-400">
      Template
    </div>
    <div className="text-[12px] font-semibold leading-4 text-slate-700">
      {selection.none ? 'Draft without a template' : (selection.title || selection.template_guid)}
    </div>
  </div>
);

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object'
);

const isDraftDocumentRow = (value: unknown): value is DraftDocumentRow => {
  if (!isRecord(value)) return false;
  return (
    value.draft_document_action !== 'inspect'
    && (
      typeof value.draft_document_guid === 'string'
      || typeof value.draft_document_job_guid === 'string'
    )
  );
};

const stringValue = (value: unknown): string | null => (
  typeof value === 'string' ? value : null
);

const numberValue = (value: unknown): number | null => (
  typeof value === 'number' && Number.isFinite(value) ? value : null
);

const recordValue = (value: unknown): Record<string, unknown> | null => (
  isRecord(value) ? value : null
);

const middleTruncatedFilenameParts = (fileName: string) => {
  const extensionMatch = fileName.match(/(\.[^.]{1,8})$/);
  if (!extensionMatch || fileName.length <= 40) {
    return { head: fileName, tail: '' };
  }

  const extension = extensionMatch[1];
  const stem = fileName.slice(0, -extension.length);
  const tailStemLength = Math.min(18, Math.max(0, stem.length - 12));
  if (tailStemLength <= 0) {
    return { head: stem, tail: extension };
  }

  return {
    head: stem.slice(0, -tailStemLength),
    tail: `${stem.slice(-tailStemLength)}${extension}`,
  };
};

const DRAFT_BUILD_STAGES = [
  {
    id: 'sources',
    label: 'Source record',
    hint: 'Collecting and reading the selected material',
    failureHint: 'Failed to collect and read the selected material',
  },
  {
    id: 'plan',
    label: 'Draft plan',
    hint: 'Settling structure, scope, and template fit',
    failureHint: 'Failed to prepare the drafting plan',
  },
  {
    id: 'author',
    label: 'Authoring',
    hint: 'Writing the legal content and section operations',
    failureHint: 'Failed to author the legal content',
  },
  {
    id: 'word',
    label: 'Word assembly',
    hint: 'Applying the draft into the DOCX surface',
    failureHint: 'Failed to assemble authored content in Word',
  },
  {
    id: 'save',
    label: 'Workspace save',
    hint: 'Saving the draft for review',
    failureHint: 'Failed to save the draft to the workspace',
  },
] as const;

type DraftBuildStageId = typeof DRAFT_BUILD_STAGES[number]['id'];

const DRAFT_BUILD_STAGE_INDEX_BY_ID: Record<DraftBuildStageId, number> = {
  sources: 0,
  plan: 1,
  author: 2,
  word: 3,
  save: 4,
};

const draftBuildStageIndexFromDetail = (detail: Record<string, unknown> | null): number | null => {
  const stage = stringValue(detail?.stage);
  if (
    stage === 'sources'
    || stage === 'plan'
    || stage === 'author'
    || stage === 'word'
    || stage === 'save'
  ) {
    return DRAFT_BUILD_STAGE_INDEX_BY_ID[stage];
  }
  return null;
};

const DRAFT_SUPPORT_EMAIL = 'support@crimson.law';

type DraftJobStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
type DraftJobTerminalErrorStatus = 'failed' | 'cancelled';

type DraftJobUiState =
  | { kind: 'document'; status: DraftJobStatus | null }
  | { kind: 'active'; status: 'queued' | 'running' }
  | { kind: 'terminalError'; status: DraftJobTerminalErrorStatus; label: string }
  | { kind: 'unknown'; status: DraftJobStatus | null };

const DRAFT_JOB_TERMINAL_ERROR_LABELS: Record<DraftJobTerminalErrorStatus, string> = {
  failed: 'Drafting could not be completed',
  cancelled: 'Drafting was cancelled',
};

const draftJobStatusFor = (status: string | null): DraftJobStatus | null => {
  switch (status) {
    case 'queued':
    case 'running':
    case 'completed':
    case 'failed':
    case 'cancelled':
      return status;
    default:
      return null;
  }
};

const isDraftJobTerminalErrorStatus = (
  status: DraftJobStatus | null,
): status is DraftJobTerminalErrorStatus => status === 'failed' || status === 'cancelled';

const draftJobUiStateFor = (statusValue: string | null, hasDocument: boolean): DraftJobUiState => {
  const status = draftJobStatusFor(statusValue);
  if (hasDocument) return { kind: 'document', status };

  switch (status) {
    case 'queued':
    case 'running':
      return { kind: 'active', status };
    case 'failed':
    case 'cancelled':
      return { kind: 'terminalError', status, label: DRAFT_JOB_TERMINAL_ERROR_LABELS[status] };
    case 'completed':
    case null:
      return { kind: 'unknown', status };
  }
};

const draftBuildStageIndex = (progressLabel: string, detail: Record<string, unknown> | null, status: DraftJobStatus | null) => {
  if (status === 'completed') return DRAFT_BUILD_STAGES.length;

  const stableStageIndex = draftBuildStageIndexFromDetail(detail);
  if (stableStageIndex !== null) return stableStageIndex;

  const qualityPass = stringValue(detail?.quality_pass);
  const normalizedLabel = progressLabel.toLowerCase();
  if (normalizedLabel.includes('saving')) return 4;
  if (
    normalizedLabel.includes('applying the word template')
    || normalizedLabel.includes('checking matter context')
    || normalizedLabel.includes('preparing legal draft')
  ) {
    return 1;
  }
  if (
    normalizedLabel.includes('word')
    || normalizedLabel.includes('applying draft')
    || qualityPass === 'docx_operations'
    || qualityPass === 'final_template_review'
  ) {
    return 3;
  }
  if (
    normalizedLabel.includes('drafting the first version')
    || normalizedLabel.includes('authoring')
    || qualityPass === 'authoring'
  ) {
    return 2;
  }
  if (
    normalizedLabel.includes('analysing')
    || normalizedLabel.includes('planning')
    || normalizedLabel.includes('template')
    || qualityPass === 'drafting_brief'
    || qualityPass === 'template_manifest'
  ) {
    return 1;
  }
  return 0;
};

const draftBuildStageFor = (
  progressLabel: string,
  detail: Record<string, unknown> | null,
  status: DraftJobStatus | null,
) => {
  const index = draftBuildStageIndex(progressLabel, detail, status);
  return DRAFT_BUILD_STAGES.find((_, stageIndex) => stageIndex === index) ?? null;
};

const draftSupportHref = ({
  chatGuid,
  errorLabel,
  jobGuid,
  matterGuid,
  stageLabel,
  status,
}: {
  chatGuid: string | null;
  errorLabel: string | null;
  jobGuid: string | null;
  matterGuid: string | null;
  stageLabel: string;
  status: DraftJobTerminalErrorStatus;
}) => {
  const outcome = status === 'cancelled' ? 'was cancelled' : 'failed';
  const context = [
    matterGuid ? `Matter GUID: ${matterGuid}` : null,
    chatGuid ? `Chat GUID: ${chatGuid}` : null,
    `Stage: ${stageLabel}`,
    jobGuid ? `Job reference: ${jobGuid}` : null,
    errorLabel ? `Error: ${errorLabel}` : null,
  ].filter((line): line is string => Boolean(line));
  const subject = jobGuid
    ? `Drafting support request — ${jobGuid}`
    : 'Drafting support request';
  const body = [
    'Hello Crimson Support,',
    '',
    `A draft ${outcome} during ${stageLabel}.`,
    '',
    ...context,
    '',
    'Please can you help?',
  ].join('\n');

  return `mailto:${DRAFT_SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

function DraftBuildPanel({
  chatGuid,
  detail,
  errorLabel,
  jobGuid,
  matterGuid,
  progressLabel,
  status,
}: {
  chatGuid: string | null;
  detail: Record<string, unknown> | null;
  errorLabel: string | null;
  jobGuid: string | null;
  matterGuid: string | null;
  progressLabel: string;
  status: DraftJobStatus | null;
}) {
  const activeStageIndex = draftBuildStageIndex(progressLabel, detail, status);
  const activeStage = DRAFT_BUILD_STAGES.find((_, index) => index === activeStageIndex) ?? null;
  const templateLabel = stringValue(detail?.template);
  const isTerminalError = isDraftJobTerminalErrorStatus(status);
  const supportHref = isTerminalError && activeStage
    ? draftSupportHref({
        chatGuid,
        errorLabel,
        jobGuid,
        matterGuid,
        stageLabel: activeStage.label,
        status,
      })
    : null;

  return (
    <div className="mt-2 max-w-4xl border-l border-slate-200/80 pl-3">
      <div className="grid max-h-96 min-w-0 gap-2.5 overflow-y-auto py-1 pr-2">
        <div className="grid min-w-0 overflow-hidden rounded-lg border border-[#dfe6f2]/90 bg-white/65">
          {DRAFT_BUILD_STAGES.map((stage, index) => {
            const isDone = activeStageIndex > index;
            const isActive = activeStageIndex === index && !isTerminalError;
            const isFailedStage = activeStageIndex === index && isTerminalError;
            return (
              <div
                key={stage.id}
                className={cn(
                  'grid min-w-0 grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-2 border-[#dfe6f2]/80 px-2 py-2',
                  index < DRAFT_BUILD_STAGES.length - 1 && 'border-b',
                  isActive && 'bg-slate-50/80',
                )}
              >
                <span className={cn(
                  'flex h-5 w-5 items-center justify-center rounded-md text-[11px] font-semibold ring-1',
                  isDone && 'bg-[#e9f8f3] text-teal-700 ring-teal-100',
                  isActive && 'bg-white text-[#596b96] ring-[#cfd9ef]',
                  isFailedStage && 'bg-rose-50 text-[#d0526b] ring-rose-100',
                  !isDone && !isActive && !isFailedStage && 'bg-[#f3f6fa] text-[#9aa6bb] ring-[#dbe3ef]',
                )}>
                  {isFailedStage ? (
                    <AlertTriangle className="h-3 w-3" />
                  ) : isDone ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    index + 1
                  )}
                </span>
                <span className="min-w-0">
                  <span className="block min-w-0 break-words text-[12px] font-semibold leading-4 text-slate-700">
                    {stage.label}
                  </span>
                  <span className="flex min-w-0 items-center gap-2.5 text-[11px] leading-4 text-slate-400">
                    <span className="min-w-0 break-words">
                      {isFailedStage
                        ? status === 'cancelled' ? 'Drafting was cancelled at this step' : stage.failureHint
                        : stage.hint}
                    </span>
                    {isFailedStage && supportHref ? (
                      <NewButton
                        href={supportHref}
                        variant="bare"
                        size="icon-sm"
                        icon={CircleQuestionMark}
                        tooltip="Contact support"
                        aria-label="Contact support"
                        className="shrink-0 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                      />
                    ) : null}
                  </span>
                </span>
              </div>
            );
          })}
        </div>

        {templateLabel ? (
          <div className="flex min-w-0 flex-wrap gap-1.5 text-[11px] leading-4 text-slate-500">
            <span className="max-w-full truncate rounded-full bg-white/70 px-2 py-0.5 ring-1 ring-slate-200/80">
              {templateLabel}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const draftDocumentRowsFromMetadata = (
  metadata: Record<string, unknown> | null | undefined,
): DraftDocumentRow[] => {
  if (!metadata) return [];
  const rows = Array.isArray(metadata.draft_documents)
    ? metadata.draft_documents
    : isDraftDocumentRow(metadata)
      ? [metadata]
      : [];
  return rows.filter(isDraftDocumentRow);
};

const isPendingDraftJobRow = (row: DraftDocumentRow): boolean => {
  if (row.draft_document_guid) return false;
  const status = String(row.draft_document_job_status || '');
  if (status === 'failed' || status === 'cancelled') return false;
  return typeof row.draft_document_job_guid === 'string';
};

const mergeDraftJobPollMetadata = (
  serverMetadata: Record<string, unknown>,
  pollMetadata: Record<string, unknown>,
  activeJobGuids: ReadonlySet<string>,
): Record<string, unknown> => {
  const serverRows = draftDocumentRowsFromMetadata(serverMetadata);
  const pollRows = draftDocumentRowsFromMetadata(pollMetadata);
  if (pollRows.length === 0 || activeJobGuids.size === 0 || serverRows.length === 0) {
    return serverMetadata;
  }

  const pollByJobGuid = new Map(
    pollRows
      .filter((row) => typeof row.draft_document_job_guid === 'string')
      .map((row) => [String(row.draft_document_job_guid), row]),
  );

  const mergedRows = serverRows.map((row) => {
    const jobGuid = typeof row.draft_document_job_guid === 'string'
      ? row.draft_document_job_guid
      : null;
    if (!jobGuid || !activeJobGuids.has(jobGuid) || !pollByJobGuid.has(jobGuid)) {
      return row;
    }

    const pollRow = pollByJobGuid.get(jobGuid)!;
    if (row.draft_document_guid) return row;

    const serverLabel = stringValue(row.draft_document_job_progress_label) || '';
    const pollLabel = stringValue(pollRow.draft_document_job_progress_label) || '';
    const serverDetail = recordValue(row.draft_document_job_progress_detail);
    const pollDetail = recordValue(pollRow.draft_document_job_progress_detail);
    const serverStatus = draftJobStatusFor(stringValue(row.draft_document_job_status));
    const pollStatus = draftJobStatusFor(stringValue(pollRow.draft_document_job_status));
    const serverStage = draftBuildStageIndex(serverLabel, serverDetail, serverStatus);
    const pollStage = draftBuildStageIndex(pollLabel, pollDetail, pollStatus);

    if (serverStage > pollStage) return row;
    if (pollStage > serverStage) return { ...row, ...pollRow };
    return { ...row, ...pollRow };
  });

  if (Array.isArray(serverMetadata.draft_documents)) {
    return { ...serverMetadata, draft_documents: mergedRows };
  }
  if (serverRows.length === 1 && mergedRows.length === 1) {
    return { ...serverMetadata, ...mergedRows[0] };
  }
  return { ...serverMetadata, draft_documents: mergedRows };
};

const formatMessageTime = (timestamp: Date) => timestamp.toLocaleTimeString(undefined, {
  hour: 'numeric',
  minute: '2-digit',
});

interface DraftDocumentCardProps {
  row: DraftDocumentRow;
  slug: string;
  matterGuid: string | null;
  chatGuid: string | null;
  defaultExpanded: boolean;
}

const DraftDocumentCard = memo(function DraftDocumentCard({
  row,
  slug,
  matterGuid,
  chatGuid,
  defaultExpanded,
}: DraftDocumentCardProps) {
  const navigate = useNavigate();
  const { showCasePartnerTab } = useSidePanel();
  const documentGuid = stringValue(row.draft_document_guid);
  const jobGuid = stringValue(row.draft_document_job_guid);
  const title = stringValue(row.draft_document_title) || 'Draft document';
  const fileName = title.toLowerCase().endsWith('.docx') ? title : `${title}.docx`;
  const fileNameParts = middleTruncatedFilenameParts(fileName);
  const rawJobStatus = stringValue(row.draft_document_job_status);
  const jobStatus = draftJobStatusFor(rawJobStatus);
  const jobState = draftJobUiStateFor(rawJobStatus, Boolean(documentGuid));
  const jobActive = jobState.kind === 'active';
  const jobTerminalError = jobState.kind === 'terminalError';
  const progressLabel = stringValue(row.draft_document_job_progress_label) || 'Legal draft in progress';
  const progressDetail = recordValue(row.draft_document_job_progress_detail);
  const activeBuildStage = draftBuildStageFor(progressLabel, progressDetail, jobStatus);
  const canShowBuildPanel = Boolean(jobGuid && (jobActive || jobTerminalError || documentGuid));
  const shouldDefaultExpand = defaultExpanded && canShowBuildPanel;
  const defaultExpandedAppliedRef = useRef(shouldDefaultExpand);
  const [previewExpanded, setPreviewExpanded] = useState(shouldDefaultExpand);
  const terminalErrorLabel = jobTerminalError ? jobState.label : 'Drafting could not be completed';
  const errorLabel = stringValue(row.draft_document_job_error) || terminalErrorLabel;
  const statusLabel = documentGuid
    ? 'Ready for review'
    : jobTerminalError ? terminalErrorLabel : activeBuildStage?.label ?? progressLabel;
  const tileClassName = cn(
    'group/draft inline-flex min-w-0 max-w-full items-center gap-2 overflow-hidden rounded-full py-1 pr-1.5 text-left transition-colors',
    'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-200',
    documentGuid && 'hover:bg-slate-50/70',
    jobActive && 'cursor-pointer hover:bg-slate-50/70',
    jobTerminalError && 'cursor-pointer hover:bg-slate-50/70',
    !documentGuid && !jobActive && !jobTerminalError && 'cursor-default',
  );

  useEffect(() => {
    if (defaultExpandedAppliedRef.current || !defaultExpanded || !canShowBuildPanel) return;
    defaultExpandedAppliedRef.current = true;
    setPreviewExpanded(true);
  }, [canShowBuildPanel, defaultExpanded]);

  const draftDocumentHref = () => {
    if (!documentGuid) return '#';
    return `/matter/${slug}/drafting/${documentGuid}`;
  };

  const handleOpenDraft = (event: React.MouseEvent<HTMLElement>) => {
    if (!documentGuid) return;

    if (
      event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) {
      return;
    }

    event.preventDefault();
    showCasePartnerTab({ activate: true, open: true });
    void navigate({
      to: '/matter/$slug/drafting/$documentGuid',
      params: { slug, documentGuid: String(documentGuid) },
    }).finally(() => {
      window.setTimeout(() => {
        showCasePartnerTab({ activate: true, open: true });
      }, 0);
    });
  };

  const handleToggleDraftPreview = () => {
    setPreviewExpanded((current) => !current);
  };

  const documentStatusIcon = jobTerminalError ? (
    <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-[#d0526b]" aria-hidden="true" />
  ) : documentGuid ? (
    <Check className="h-3.5 w-3.5 shrink-0 text-teal-700" aria-hidden="true" />
  ) : null;
  const tileMainContent = (
    <>
      <span className="relative shrink-0">
        <FileWordIcon className="h-[18px] w-[18px] text-[#4F78B5]" />
        {jobActive ? (
          <span
            className="absolute -right-0.5 -top-1 h-2 w-2 animate-pulse rounded-full bg-primary-400 motion-reduce:animate-none"
            aria-hidden="true"
          />
        ) : null}
      </span>
      <span className="flex min-w-0 flex-1 items-baseline text-xs font-medium leading-5 text-slate-600">
        <span className="min-w-0 truncate">
          {fileNameParts.head}
        </span>
        {fileNameParts.tail ? (
          <span className="shrink-0">
            {fileNameParts.tail}
          </span>
        ) : null}
      </span>
      <span className="h-3 w-px shrink-0 bg-slate-200" />
      <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
        {jobActive ? (
          <Spinner
            size="xs"
            className="shrink-0 border-[#8FA0D0] border-r-transparent"
            aria-label="Legal draft in progress"
          />
        ) : null}
        {documentStatusIcon}
        <span className={cn(
          'text-xs leading-5 text-slate-400/90',
          jobTerminalError && 'text-[#d0526b]',
        )}>
          {statusLabel}
        </span>
      </span>
    </>
  );
  const buildPanelToggleIcon = previewExpanded ? (
    <ChevronDown className="h-3.5 w-3.5" />
  ) : (
    <ChevronRight className="h-3.5 w-3.5" />
  );
  const buildPanelToggleButton = canShowBuildPanel ? (
    <button
      type="button"
      className="ml-0.5 shrink-0 text-slate-400 transition-colors hover:text-slate-600 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-200"
      aria-expanded={previewExpanded}
      aria-label={previewExpanded ? 'Hide draft build process' : 'Show draft build process'}
      onClick={handleToggleDraftPreview}
    >
      {buildPanelToggleIcon}
    </button>
  ) : documentGuid ? (
    <span className="ml-0.5 shrink-0 text-slate-400 transition-colors group-hover/draft:text-slate-600">
      <ChevronRight className="h-3.5 w-3.5" />
    </span>
  ) : null;

  if (!documentGuid) {
    if (jobActive || jobTerminalError) {
      return (
        <div className="min-w-0 max-w-full overflow-hidden">
          <button
            type="button"
            className={tileClassName}
            title={statusLabel}
            aria-expanded={previewExpanded}
            onClick={handleToggleDraftPreview}
          >
            {tileMainContent}
            <span className="ml-0.5 shrink-0 text-slate-400 transition-colors group-hover/draft:text-slate-600">
              {buildPanelToggleIcon}
            </span>
          </button>
          {previewExpanded ? (
            <DraftBuildPanel
              chatGuid={chatGuid}
              detail={progressDetail}
              errorLabel={errorLabel}
              jobGuid={jobGuid}
              matterGuid={matterGuid}
              progressLabel={progressLabel}
              status={jobStatus ?? (jobTerminalError ? jobState.status : 'running')}
            />
          ) : null}
        </div>
      );
    }

    return (
      <div
        className={tileClassName}
        title={statusLabel}
      >
        {tileMainContent}
      </div>
    );
  }

  if (canShowBuildPanel) {
    return (
      <div className="min-w-0 max-w-full overflow-hidden">
        <div className={tileClassName} title={fileName}>
          <a
            href={draftDocumentHref()}
            onClick={handleOpenDraft}
            className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden"
          >
            {tileMainContent}
          </a>
          {buildPanelToggleButton}
        </div>
        {previewExpanded ? (
          <DraftBuildPanel
            chatGuid={chatGuid}
            detail={progressDetail}
            errorLabel={errorLabel}
            jobGuid={jobGuid}
            matterGuid={matterGuid}
            progressLabel={progressLabel}
            status={jobStatus ?? 'completed'}
          />
        ) : null}
      </div>
    );
  }

  return (
    <a
      href={draftDocumentHref()}
      onClick={handleOpenDraft}
      className={tileClassName}
      title={fileName}
    >
      {tileMainContent}
      {buildPanelToggleButton}
    </a>
  );
});

/**
 * Renders a single chat message (user or assistant)
 * Includes thinking steps, content, citations, timestamp and copy button
 */
type ViewBundleLinkProps = {
  bundleUrl: string;
  bundleTitle: string;
  bundleNoun: string;
  variant: 'full' | 'compact';
};

/** Isolated so only messages that actually have a bundle link subscribe to route / side-panel
 *  updates. Putting those hooks on MessageItem forced every transcript row to rerender on
 *  panel-width animation frames. */
const ViewBundleLink = ({ bundleUrl, bundleTitle, bundleNoun, variant }: ViewBundleLinkProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { showCasePartnerTab } = useSidePanel();
  const normalizedPath = pathname.replace(/\/+$/, '');
  const isOnThisBundle = normalizedPath === bundleUrl || normalizedPath.startsWith(`${bundleUrl}/`);
  if (isOnThisBundle) return null;

  if (variant === 'compact') {
    return (
      <NewButton
        variant="outline"
        size="sm"
        icon={FileText}
        text={`View ${bundleNoun}: ${bundleTitle}`}
        href={bundleUrl}
        onClick={(event) => {
          if (
            event.button !== 0
            || event.metaKey
            || event.ctrlKey
            || event.shiftKey
            || event.altKey
          ) {
            return;
          }
          event.preventDefault();
          showCasePartnerTab({ activate: true, open: true });
          // Navigate to the raw (already-localized) URL so US matters stay on
          // /binders rather than being redirected to the /bundles alias.
          void navigate({ to: bundleUrl }).finally(() => {
            window.setTimeout(() => {
              showCasePartnerTab({ activate: true, open: true });
            }, 0);
          });
        }}
      />
    );
  }

  return (
    <NewButton
      variant="outline"
      size="sm"
      icon={FileText}
      text={`View ${bundleNoun}: ${bundleTitle}`}
      href={bundleUrl}
      target="_blank"
    />
  );
};


type ViewDeadlineLinkProps = {
  deadlineUrl: string;
  deadlineTitle: string;
  variant: 'full' | 'compact';
};

const ViewDeadlineLink = ({ deadlineUrl, deadlineTitle, variant }: ViewDeadlineLinkProps) => {
  const navigate = useNavigate();
  const { showCasePartnerTab } = useSidePanel();
  // Keep the button even on the deadlines page so taskId deep-links can re-open the panel.
  if (variant === 'compact') {
    return (
      <NewButton
        variant="outline"
        size="sm"
        icon={Calendar}
        text={`View deadline: ${deadlineTitle}`}
        href={deadlineUrl}
        onClick={(event) => {
          if (
            event.button !== 0
            || event.metaKey
            || event.ctrlKey
            || event.shiftKey
            || event.altKey
          ) {
            return;
          }
          event.preventDefault();
          showCasePartnerTab({ activate: true, open: true });
          void navigate({ to: deadlineUrl }).finally(() => {
            window.setTimeout(() => {
              showCasePartnerTab({ activate: true, open: true });
            }, 0);
          });
        }}
      />
    );
  }

  return (
    <NewButton
      variant="outline"
      size="sm"
      icon={Calendar}
      text={`View deadline: ${deadlineTitle}`}
      href={deadlineUrl}
      target="_blank"
    />
  );
};

const MessageItemComponent: React.FC<MessageItemProps> = ({
  message,
  documents,
  matterGuid,
  chatGuid,
  slug,
  isCopied,
  onCopy,
  onSaveToLibrary,
  renderCitedDocs,
  wordExportContent,
  wordExportFilename,
  wordExportIncludeCitations = true,
  draftCardsDefaultExpanded = false,
  quickReplyQuestions,
  quickReplyDisabled = false,
  onQuickReplySubmit,
  templateChoices,
  templateChoiceDisabled = false,
  onTemplateChoiceSubmit,
  variant = 'full',
}) => {
  const isUser = message.role === 'user';
  const [isExportingToWord, setIsExportingToWord] = useState(false);
  const { jurisdiction } = useMatterJurisdiction();
  const bundleNoun = jurisdiction.ui_labels.bundle_singular;
  const [draftJobMetadata, setDraftJobMetadata] = useState<Record<string, unknown> | null>(null);
  const messageMetadata = useMemo(
    () => (isRecord(message.message_metadata) ? message.message_metadata : {}),
    [message.message_metadata],
  );
  const quotedParts = useMemo(
    () => quotedPartsFromMessage(message.content, messageMetadata),
    [message.content, messageMetadata],
  );
  const quickReplyAnswerRows = useMemo(
    () => quickReplyAnswersFromMetadata(messageMetadata),
    [messageMetadata],
  );
  const templateChoiceSelection = useMemo(
    () => templateChoiceFromMetadata(messageMetadata),
    [messageMetadata],
  );
  const isQuickReplyAnswerMessage = isUser && quickReplyAnswerRows.length > 0;
  const isTemplateChoiceAnswerMessage = isUser && Boolean(templateChoiceSelection);
  const isStructuredAnswerMessage = isQuickReplyAnswerMessage || isTemplateChoiceAnswerMessage;
  const serverDraftDocumentRows = useMemo(
    () => draftDocumentRowsFromMetadata(messageMetadata),
    [messageMetadata],
  );
  const effectiveMetadata = useMemo<Record<string, unknown>>(() => {
    if (!draftJobMetadata) return messageMetadata;
    const pollJobGuids = new Set(
      draftDocumentRowsFromMetadata(draftJobMetadata)
        .map((row) => row.draft_document_job_guid)
        .filter((jobGuid): jobGuid is string => typeof jobGuid === 'string' && jobGuid.length > 0),
    );
    if (pollJobGuids.size === 0) return messageMetadata;
    return mergeDraftJobPollMetadata(messageMetadata, draftJobMetadata, pollJobGuids);
  }, [draftJobMetadata, messageMetadata]);
  const pendingDraftJobGuids = useMemo(
    () => serverDraftDocumentRows
      .filter(isPendingDraftJobRow)
      .map((row) => String(row.draft_document_job_guid)),
    [serverDraftDocumentRows],
  );
  const pendingDraftJobKey = pendingDraftJobGuids.join('|');
  const bundleUrl = stringValue(effectiveMetadata.bundle_url) || undefined;
  const bundleTitle = stringValue(effectiveMetadata.bundle_title) || undefined;
  const showBundleButton = Boolean(bundleUrl && bundleTitle);
  const deadlineUrl = stringValue(effectiveMetadata.deadline_url) || undefined;
  const deadlineTitle = stringValue(effectiveMetadata.deadline_title) || undefined;
  const showDeadlineButton = Boolean(deadlineUrl && deadlineTitle);
  const draftDocumentRows = useMemo<DraftDocumentRow[]>(
    () => draftDocumentRowsFromMetadata(effectiveMetadata),
    [effectiveMetadata],
  );
  const generationStopped = Boolean(message.message_metadata?.generation_stopped);
  const streamInterrupted = Boolean(message.message_metadata?.stream_interrupted);
  const [sourcesReady, setSourcesReady] = useState(generationStopped);
  const webCitationMeta = useMemo(
    () => collectWebCitationMeta(message.thinkingSteps),
    [message.thinkingSteps],
  );
  const timelineItems = useMemo(
    () => timelineFromCompletedMessage(
      message.thinkingSteps,
      message.content,
      message.timelineInsertions,
      message.waitMarkers ?? timelineWaitMarkersFromMetadata(
        effectiveMetadata.lead_wait_markers,
        numberValue(effectiveMetadata.lead_waited_duration_ms),
      ),
    ),
    [
      message.thinkingSteps,
      message.content,
      message.timelineInsertions,
      message.waitMarkers,
      effectiveMetadata,
    ],
  );
  // Copy and Word export mean the response, not the commentary the lead wrote
  // while it worked. Empty when a turn was interrupted before it answered, which
  // hides both affordances: there is no response to take away.
  const answerText = useMemo(
    () => (
      isUser
        ? message.content
        : answerTextFromTimeline(timelineItems, {
          stopped: generationStopped || streamInterrupted,
        })
    ),
    [isUser, message.content, timelineItems, generationStopped, streamInterrupted],
  );

  useEffect(() => {
    if (!draftJobMetadata) return;
    if (serverDraftDocumentRows.length === 0) return;
    const serverFullyFresh = serverDraftDocumentRows.every((row) => (
      Boolean(row.draft_document_guid)
      || ['failed', 'cancelled'].includes(String(row.draft_document_job_status || ''))
    ));
    if (serverFullyFresh) {
      setDraftJobMetadata(null);
    }
  }, [draftJobMetadata, serverDraftDocumentRows]);

  useEffect(() => {
    if (!pendingDraftJobKey || !matterGuid) return;

    let cancelled = false;
    const jobGuids = pendingDraftJobKey.split('|').filter(Boolean);
    const poll = async () => {
      const updates: Record<string, Record<string, unknown>> = {};
      await Promise.all(jobGuids.map(async (jobGuid: string) => {
        try {
          const response = await backendClient.get(`/case-partner/draft-jobs/${jobGuid}`, {
            params: { matter_guid: matterGuid },
          });
          const metadata = response.data?.metadata;
          if (metadata && typeof metadata === 'object') {
            updates[jobGuid] = {
              ...metadata,
              draft_document_job_progress_detail: response.data?.progress_detail ?? null,
            };
          }
        } catch (error) {
          if (!cancelled) {
            console.info('Draft generation job status unavailable:', error);
          }
        }
      }));

      if (cancelled || Object.keys(updates).length === 0) return;
      const pollRows = jobGuids
        .map((jobGuid) => updates[jobGuid])
        .filter((row): row is Record<string, unknown> => Boolean(row));
      if (pollRows.length === 0) return;

      if (pollRows.length === 1) {
        setDraftJobMetadata(pollRows[0]);
        return;
      }
      setDraftJobMetadata({ draft_documents: pollRows });
    };

    void poll();
    const intervalId = window.setInterval(poll, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [matterGuid, pendingDraftJobKey]);

  const handleExportToWord = async (
    includeCitations: boolean,
    exportContent = answerText,
    filename?: string,
  ) => {
    if (!exportContent) return;
    if (!matterGuid) {
      toast.error('Matter is still loading. Please try again in a moment.');
      return;
    }

    setIsExportingToWord(true);
    try {
      const citationSuffix = includeCitations ? '-with-citations' : '';
      await exportMarkdownAsDocx(
        matterGuid,
        exportContent,
        includeCitations,
        filename || `case-partner-response${citationSuffix}-${Date.now()}.docx`,
      );
    } catch (error: unknown) {
      console.error('Error exporting to Word:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to export to Word');
    } finally {
      setIsExportingToWord(false);
    }
  };

  const renderMetaRow = (className?: string) => (
    <div className={cn(`flex items-center mt-1 ${isUser ? 'justify-end' : 'justify-start'}`, className)}>
      <p className="text-xs text-slate-400">{formatMessageTime(message.timestamp)}</p>
      {generationStopped && (
        <p className="ml-2 text-xs text-slate-400">Stopped</p>
      )}

      {answerText && (
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6 rounded-full ml-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
          onClick={() => onCopy(answerText, message.id)}
          title={isCopied ? 'Copied!' : 'Copy to clipboard'}
        >
          {isCopied ? <ClipboardCheck className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
        </Button>
      )}

      {message.content && isUser && !isStructuredAnswerMessage && onSaveToLibrary && (
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6 rounded-full ml-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
          onClick={() => onSaveToLibrary(message.content)}
          title="Save to prompt library"
        >
          <BookmarkPlus className="h-3 w-3" />
        </Button>
      )}

      {answerText && message.role === 'assistant' && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 rounded-full ml-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
              disabled={isExportingToWord || !matterGuid}
              title="Download as Word document"
            >
              <FileDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem
              onClick={() => handleExportToWord(true)}
              disabled={isExportingToWord || !matterGuid}
            >
              With citations
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleExportToWord(false)}
              disabled={isExportingToWord || !matterGuid}
            >
              Without citations
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );

  return (
    <div
      key={message.id || message.timestamp.getTime()}
      data-message-id={message.id}
      data-message-ids={(message.messageIds || (typeof message.id === 'number' ? [message.id] : [])).join(' ')}
      className="group min-w-0 max-w-full overflow-hidden"
    >
      <div className={`flex min-w-0 items-start ${isUser ? 'justify-end' : 'justify-start'}`}>
        <div
          className={cn(
            'relative min-w-0 max-w-full group',
            !isStructuredAnswerMessage && 'rounded-2xl px-4 py-3',
            isUser
              ? isStructuredAnswerMessage
                ? ''
                : 'bg-slate-100 rounded-tr-none'
              : 'w-full min-w-0 overflow-hidden bg-transparent'
          )}
        >
          <div className="min-w-0 max-w-full">
            {isUser ? (
              templateChoiceSelection ? (
                <TemplateChoiceAnswerSummary selection={templateChoiceSelection} />
              ) : quickReplyAnswerRows.length > 0 ? (
                <QuickReplyAnswerSummary answers={quickReplyAnswerRows} />
              ) : quotedParts ? (
                <div className="flex flex-col gap-2">
                  <QuotedExcerpt text={quotedParts.excerpt} compact />
                  {quotedParts.question ? (
                    <div className="prose prose-slate max-w-none wrap-break-word whitespace-pre-wrap">
                      {quotedParts.question}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="prose prose-slate max-w-none wrap-break-word whitespace-pre-wrap">
                  {message.content}
                </div>
              )
            ) : (
              <TurnTimeline
                documents={documents}
                items={timelineItems}
                onSourcesReady={setSourcesReady}
                reasoningDurationMs={numberValue(effectiveMetadata.reasoning_duration_ms)}
                slug={slug}
                stopRequested={generationStopped}
                webCitationMeta={webCitationMeta}
              />
            )}
          </div>

          {!isUser && templateChoices && templateChoices.templates.length > 0 && onTemplateChoiceSubmit && (
            <div className="mt-5 mb-5">
              <TemplateChoicesPanel
                allowNone={templateChoices.allowNone}
                disabled={templateChoiceDisabled}
                matterGuid={matterGuid}
                onSubmit={onTemplateChoiceSubmit}
                question={templateChoices.question}
                templates={templateChoices.templates}
              />
            </div>
          )}

          {!isUser && quickReplyQuestions && quickReplyQuestions.length > 0 && onQuickReplySubmit && (
            <div className="mt-5 mb-5">
              <QuickRepliesPanel
                disabled={quickReplyDisabled}
                onSubmit={onQuickReplySubmit}
                questions={quickReplyQuestions}
              />
            </div>
          )}

          {!isUser && renderMetaRow('case-partner-inline-meta')}

          {/* Generated artifact link buttons */}
          {(showBundleButton || showDeadlineButton || wordExportContent) && (
            <div className="mt-3 flex flex-wrap gap-2">
              {showBundleButton && bundleUrl && bundleTitle && (
                <ViewBundleLink
                  bundleUrl={bundleUrl}
                  bundleTitle={bundleTitle}
                  bundleNoun={bundleNoun}
                  variant={variant}
                />
              )}
              {showDeadlineButton && deadlineUrl && deadlineTitle && (
                <ViewDeadlineLink
                  deadlineUrl={deadlineUrl}
                  deadlineTitle={deadlineTitle}
                  variant={variant}
                />
              )}
              {wordExportContent && (
                <NewButton
                  variant="outline"
                  size="sm"
                  icon={FileDown}
                  text="Download Word"
                  onClick={() => handleExportToWord(
                    wordExportIncludeCitations,
                    wordExportContent,
                    wordExportFilename,
                  )}
                  disabled={isExportingToWord || !matterGuid}
                  loading={isExportingToWord}
                  title={wordExportFilename || 'Download as Word document'}
                />
              )}
            </div>
          )}

          {draftDocumentRows.length > 0 && (
            <div className="mt-5 grid w-full min-w-0 max-w-[40rem] gap-2 overflow-hidden">
              {draftDocumentRows.map((row) => {
                const documentGuid = stringValue(row.draft_document_guid);
                const jobGuid = stringValue(row.draft_document_job_guid);
                const title = stringValue(row.draft_document_title) || 'Draft document';
                return (
                  <DraftDocumentCard
                    key={jobGuid ? `${jobGuid}:${documentGuid ?? ''}` : documentGuid || title}
                    row={row}
                    slug={slug}
                    matterGuid={matterGuid ?? null}
                    chatGuid={chatGuid ?? null}
                    defaultExpanded={draftCardsDefaultExpanded && draftDocumentRows.length === 1}
                  />
                );
              })}
            </div>
          )}

          {/* Cited documents section - inside message bubble */}
          {message.role === 'assistant' &&
            message.content &&
            renderCitedDocs &&
            sourcesReady &&
            (
              <div className="case-partner-cited-docs">
                {renderCitedDocs(message.content, webCitationMeta)}
              </div>
            )}

          {!isUser && renderMetaRow('case-partner-bottom-meta !mt-3')}
        </div>
      </div>

      {/* Timestamp and action buttons */}
      {isUser && renderMetaRow()}
    </div>
  );
};

export const MessageItem = memo(MessageItemComponent);

export default MessageItem;
